import { useRef, useState } from "react";
import type { NamedView, View } from "@common/types/views/views";
import { ViewType } from "@common/types/views/views";
import { Button } from "@design-system/Button/Button";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ViewsDropdown } from "@design-system/ViewToolbar/ViewsDropdown";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import layoutStyles from "./DatePickerPlayground.module.scss";
import styles from "./FiltersToolbarPlayground.module.scss";

type SortOption = "date-desc" | "date-asc" | "amount-desc";

const sortOptions: ReadonlyArray<{ value: SortOption; label: string }> = [
    { value: "date-desc", label: "Newest first" },
    { value: "date-asc", label: "Oldest first" },
    { value: "amount-desc", label: "Highest amount" },
];

const builtInViews: Array<NamedView<View>> = [
    {
        id: "all-transactions",
        name: "All transactions",
        type: ViewType.TRANSACTION,
        columns: [],
        filters: {},
    },
    {
        id: "needs-review",
        name: "Needs review",
        type: ViewType.TRANSACTION,
        columns: [],
        filters: {},
    },
];

const initialSavedViews: Array<NamedView<View>> = [
    {
        id: "month-end",
        name: "Month-end review",
        type: ViewType.TRANSACTION,
        columns: [],
        filters: {},
    },
];

export const ViewToolbarPlayground = () => {
    const generatedViewId = useRef(0);
    const [selectedView, setSelectedView] = useState<NamedView<View> | null>(
        builtInViews[0] ?? null,
    );
    const [savedViews, setSavedViews] =
        useState<Array<NamedView<View>>>(initialSavedViews);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<SortOption>("date-desc");
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [lastAction, setLastAction] = useState("Ready");

    const createView = () => {
        generatedViewId.current += 1;
        const newView: NamedView<View> = {
            id: `custom-view-${generatedViewId.current}`,
            name: `Custom review ${savedViews.length + 1}`,
            type: ViewType.TRANSACTION,
            columns: [],
            filters: {},
        };
        setSavedViews((currentViews) => [...currentViews, newView]);
        setSelectedView(newView);
        setLastAction(`Created ${newView.name}`);
    };

    const duplicateView = (view: NamedView<View>) => {
        generatedViewId.current += 1;
        const duplicate: NamedView<View> = {
            ...view,
            id: `${view.id}-copy-${generatedViewId.current}`,
            name: `${view.name} copy`,
        };
        setSavedViews((currentViews) => [...currentViews, duplicate]);
        setSelectedView(duplicate);
        setLastAction(`Duplicated ${view.name}`);
    };

    const renameView = (view: NamedView<View>) => {
        const renamedView = { ...view, name: `${view.name} updated` };
        setSavedViews((currentViews) =>
            currentViews.map((currentView) =>
                currentView.id === view.id ? renamedView : currentView,
            ),
        );
        setSelectedView(renamedView);
        setLastAction(`Renamed ${view.name}`);
    };

    const deleteView = (view: NamedView<View>) => {
        setSavedViews((currentViews) =>
            currentViews.filter((currentView) => currentView.id !== view.id),
        );
        if (selectedView?.id === view.id) {
            setSelectedView(builtInViews[0] ?? null);
        }
        setLastAction(`Deleted ${view.name}`);
    };

    return (
        <SandboxLayout
            controls={
                <section>
                    <h2>Features</h2>
                    <div className={layoutStyles.booleanGrid}>
                        <BooleanControl
                            label="Disabled"
                            checked={isDisabled}
                            onChange={setIsDisabled}
                        />
                    </div>
                </section>
            }
        >
            <ShowcasePreview
                summary={lastAction}
                width="full"
                output={JSON.stringify(
                    {
                        props: {
                            disabled: isDisabled,
                            selectedView: selectedView?.name ?? null,
                            savedViewCount: savedViews.length,
                        },
                        showcaseState: {
                            search,
                            sort,
                            isFilterActive,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.toolbarSurface}>
                    <div className={styles.toolbar}>
                        <ViewsDropdown
                            builtInViews={builtInViews}
                            savedViews={savedViews}
                            selectedView={selectedView}
                            onViewSelect={(view) => {
                                setSelectedView(view);
                                setLastAction(
                                    view
                                        ? `Selected ${view.name}`
                                        : "Cleared view",
                                );
                            }}
                            onViewCreate={createView}
                            onViewDelete={deleteView}
                            onViewRename={renameView}
                            onViewDuplicate={duplicateView}
                            className={styles.viewDropdown}
                            disabled={isDisabled}
                        />
                        <div className={styles.toolbarSearch}>
                            <SearchFilter
                                filter={search}
                                onChange={(value) => setSearch(value ?? "")}
                                placeholder="Search transactions..."
                                disabled={isDisabled}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            disabled={isDisabled}
                            className={
                                isFilterActive ? styles.activeFilter : undefined
                            }
                            onClick={() =>
                                setIsFilterActive((isActive) => !isActive)
                            }
                        >
                            {isFilterActive ? "Account: Operating" : "Filter"}
                        </Button>
                        <PlaygroundSelect<SortOption>
                            options={sortOptions}
                            value={sort}
                            onChange={setSort}
                            disabled={isDisabled}
                        />
                    </div>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
